import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  Button,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import BuildIcon from '@mui/icons-material/Build';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import LaptopIcon from '@mui/icons-material/Laptop';
import TvIcon from '@mui/icons-material/Tv';

const IssuesList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    // Simulate loading data
    setTimeout(() => {
      setIssues([
        {
          id: 1,
          title: 'iPhone 13 not charging properly',
          description: 'My iPhone 13 only charges when the cable is positioned at a specific angle. I\'ve tried multiple cables and the issue persists.',
          category: 'Smartphone',
          brand: 'Apple',
          status: 'open',
          responses: 2,
          created_at: '2023-08-15T10:30:00Z'
        },
        {
          id: 2,
          title: 'Dell XPS 15 overheating during gaming',
          description: 'My laptop gets extremely hot when playing games, even with a cooling pad. The fans are very loud and the system sometimes shuts down.',
          category: 'Laptop',
          brand: 'Dell',
          status: 'in progress',
          responses: 5,
          created_at: '2023-08-14T15:45:00Z'
        },
        {
          id: 3,
          title: 'Samsung TV has vertical lines on screen',
          description: 'My 55" Samsung TV suddenly developed vertical lines across the entire screen. They\'re present on all inputs and even when no source is connected.',
          category: 'TV',
          brand: 'Samsung',
          status: 'solved',
          responses: 8,
          created_at: '2023-08-10T09:15:00Z'
        }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  // Get icon based on category
  const getCategoryIcon = (category) => {
    switch (category.toLowerCase()) {
      case 'smartphone':
        return <SmartphoneIcon />;
      case 'laptop':
        return <LaptopIcon />;
      case 'tv':
        return <TvIcon />;
      default:
        return <BuildIcon />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold',
            color: '#006938',
            mb: 4
          }}
        >
          Community Issues & Solutions
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress sx={{ color: '#006938' }} />
          </Box>
        ) : issues.length === 0 ? (
          <Alert severity="info">No issues found. Be the first to post a question!</Alert>
        ) : (
          <Grid container spacing={3}>
            {issues.map((issue) => (
              <Grid item xs={12} md={6} lg={4} key={issue.id}>
                <Card sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                  }
                }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Chip 
                        icon={getCategoryIcon(issue.category)} 
                        label={issue.category} 
                        size="small"
                        sx={{ 
                          bgcolor: 'rgba(0, 105, 56, 0.1)', 
                          color: '#006938' 
                        }}
                      />
                      <Chip 
                        label={issue.status} 
                        size="small"
                        color={
                          issue.status === 'solved' ? 'success' : 
                          issue.status === 'in progress' ? 'warning' : 
                          'default'
                        }
                      />
                    </Box>
                    
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {issue.title}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {issue.description.length > 120 
                        ? `${issue.description.substring(0, 120)}...` 
                        : issue.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(issue.created_at)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {issue.responses} responses
                      </Typography>
                    </Box>
                  </CardContent>
                  
                  <Box sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => navigate(`/issues/view/${issue.id}`)}
                      sx={{ 
                        borderColor: '#006938', 
                        color: '#006938',
                        '&:hover': {
                          borderColor: '#004d2b',
                          bgcolor: 'rgba(0, 105, 56, 0.04)'
                        }
                      }}
                    >
                      View Details
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button
            variant="contained"
            onClick={() => navigate('/issues')}
            sx={{ 
              bgcolor: '#006938',
              '&:hover': {
                bgcolor: '#004d2b'
              }
            }}
          >
            Post New Issue
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default IssuesList;
